import { Fee, FeeCondition } from '../models/fee.model';
import { Quote, QuoteFee } from '../models/quote.model';
import { State } from '../models/state.model';
import { parseMoney } from './format.util';

export type FeeParameterKind = 'text' | 'number' | 'select';

export interface FeeParameter {
  field: string;
  label: string;
  kind: FeeParameterKind;
  options?: string[];
  /** Value can hold several choices, stored comma-separated; "=" means any of them, "!=" none of them. */
  multiple?: boolean;
}

export const QUOTE_COLLATERAL_TYPES = [
  'Vehicle',
  'Motorcycle',
  'Boat',
  'Boat Trailer',
  'RV',
  'Motorhome',
  'Truck Camper Slide on',
  'Pop Up / Tent w/ Axle',
  'Utility Trailer',
  'Cargo Trailer',
  'Dump Trailer',
  'Horse Trailer',
  'Toy Hauler',
  'Powersports Trailer',
];

/** Quote fields a fee condition can test. Add a row here to expose a new parameter. */
export const FEE_PARAMETERS: FeeParameter[] = [
  { field: 'collateralType', label: 'Collateral', kind: 'select', options: QUOTE_COLLATERAL_TYPES, multiple: true },
  { field: 'city', label: 'City', kind: 'text' },
  { field: 'transactionType', label: 'Transaction Type', kind: 'text' },
  { field: 'outsideCityLimits', label: 'Outside City Limits', kind: 'select', options: ['Yes', 'No'] },
  { field: 'plateTransfer', label: 'Plate Transfer', kind: 'select', options: ['Yes', 'No'] },
  { field: 'daysSincePurchase', label: 'Days Since Purchase', kind: 'number' },
  { field: 'lien', label: 'Lien', kind: 'select', options: ['Yes', 'No'] },
  { field: 'basePrice', label: 'Base Price', kind: 'number' },
  { field: 'tradeInValue', label: 'Trade-In Value', kind: 'number' },
  { field: 'modelYear', label: 'Model Year', kind: 'number' },
  { field: 'vehicleAge', label: 'Vehicle Age (years)', kind: 'number' },
  { field: 'weight', label: 'Weight', kind: 'number' },
];

export const getFeeParameter = (field: string) => FEE_PARAMETERS.find((p) => p.field === field);

export interface FeeResult {
  fee: Fee;
  amount: number;
}

const toNumber = parseMoney;

const quoteValue = (quote: Partial<Quote>, field: string): string => {
  if (field === 'daysSincePurchase') {
    const purchased = Date.parse(quote.purchaseDate ?? '');
    return Number.isNaN(purchased) ? '' : String(Math.floor((Date.now() - purchased) / 86400000));
  }
  if (field === 'vehicleAge') {
    const year = toNumber(quote.modelYear);
    return year > 0 ? String(new Date().getFullYear() - year) : '';
  }
  const raw = (quote as Record<string, unknown>)[field];
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  return String(raw ?? '').trim();
};

export const conditionMatches = (condition: FeeCondition, quote: Partial<Quote>): boolean => {
  const actual = quoteValue(quote, condition.field);
  if (getFeeParameter(condition.field)?.kind === 'number') {
    const a = toNumber(actual);
    const b = toNumber(condition.value);
    if (condition.operator === '<=') return a <= b;
    if (condition.operator === '>=') return a >= b;
    if (condition.operator === '!=') return a !== b;
    return a === b;
  }
  const choices = condition.value.split(',').map((v) => v.trim().toLowerCase());
  const equal = choices.includes(actual.toLowerCase());
  return condition.operator === '!=' ? !equal : equal;
};

/** A fee applies when every one of its conditions matches; no conditions means it always applies. */
export const feeApplies = (fee: Fee, quote: Partial<Quote>): boolean =>
  (fee.conditions ?? []).every((c) => conditionMatches(c, quote));

export const feeAmount = (fee: Fee, quote: Partial<Quote>): number => {
  const cost = toNumber(fee.cost);
  if (fee.type !== 'percentage') return cost;
  // "per100" rates are dollars per $100 of base price (e.g. KY/LA motor vehicle tax), otherwise a plain percent
  const price = toNumber(quote.basePrice);
  return fee.rateUnit === 'per100' ? (price / 100) * cost : (price * cost) / 100;
};

/** Statewide fees plus fees of the quote's county, filtered by their conditions. */
export const calculateFees = (state: State | undefined, quote: Partial<Quote>): FeeResult[] => {
  if (!state) return [];
  const county = state.counties.find((c) => c.name === quote.county);
  return [...state.fees, ...(county?.fees ?? [])]
    .filter((fee) => feeApplies(fee, quote))
    .map((fee) => ({ fee, amount: feeAmount(fee, quote) }));
};

/** The applicable fees as stored on a quote, so later rule edits don't change a saved quote. */
export const snapshotFees = (state: State | undefined, quote: Partial<Quote>): QuoteFee[] =>
  calculateFees(state, quote).map(({ fee, amount }) => ({ name: fee.name, type: fee.type, amount }));

export const describeConditions = (conditions: FeeCondition[] | undefined): string =>
  (conditions ?? [])
    .map((c) => {
      const param = getFeeParameter(c.field);
      const op = param?.multiple ? (c.operator === '!=' ? 'is none of' : 'is any of') : c.operator;
      return `${param?.label ?? c.field} ${op} ${c.value.split(',').join(', ')}`;
    })
    .join(' and ');
